import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  TextField,
  Divider,
  useTheme,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import {
  Receipt as ReceiptIcon,
  Description as InvoiceIcon,
} from '@mui/icons-material';
import config from '../config';
import { useAuth } from '../contexts/AuthContext';
import ManualReceiptEntryDialog from './ManualReceiptEntryDialog';
import UploadCaptureControls from './UploadCaptureControls';

type DocType = 'receipt' | 'invoice';
type PickerMode = 'new' | 'existing';

interface LinkTarget {
  bank_transaction_id?: string;
  bank_statement_transaction_id?: string;
}

interface UploadProofDialogProps {
  open: boolean;
  onClose: () => void;
  linkTarget: LinkTarget;
  onSuccess: () => void;
}

interface ExistingDoc {
  id: string;
  contractor_name: string;
  total: string;
  invoice_date: string;
}

// Module-level (not defined inside UploadProofDialog) so it isn't recreated as a new component
// type on every render, which would otherwise remount every thumbnail and drop its onError state
// each time the parent re-renders. Falls back to a plain icon for manually-entered documents
// (no underlying photo/PDF to preview) or any load failure, same pattern as StatementThumbnail.
const ExistingDocThumb: React.FC<{ id: string; docType: DocType }> = ({ id, docType }) => {
  const [errored, setErrored] = useState(false);
  const boxSx = { width: 32, height: 40, borderRadius: 0.75, flexShrink: 0 } as const;
  if (errored) {
    return (
      <Box sx={{ ...boxSx, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover' }}>
        {docType === 'receipt' ? <ReceiptIcon sx={{ fontSize: 16, color: 'text.disabled' }} /> : <InvoiceIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
      </Box>
    );
  }
  return (
    <img
      src={`${config.apiUrl}/api/invoices/${id}/preview`}
      alt=""
      onError={() => setErrored(true)}
      style={{ ...boxSx, objectFit: 'cover' }}
    />
  );
};

// Reconciliation: attaches an existing receipt/invoice as "proof" of one specific bank
// transaction (live TrueLayer feed or an uploaded statement row) — a deliberately narrower,
// single-file version of ReceiptUpload.tsx/InvoiceUpload.tsx (which are bulk multi-file dropzones
// for the general Invoices section) since this always targets exactly one transaction. Reuses
// the same upload endpoints with the link ids attached, so OCR/Gemini extraction, dedup, and the
// manual-entry fallback all come for free.
const UploadProofDialog: React.FC<UploadProofDialogProps> = ({ open, onClose, linkTarget, onSuccess }) => {
  const { palette } = useTheme();
  const { token } = useAuth();
  const [docType, setDocType] = useState<DocType | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicateInvoice, setDuplicateInvoice] = useState<any | null>(null);
  const [manualEntryFileInfo, setManualEntryFileInfo] = useState<any | null>(null);
  const [manualEntryError, setManualEntryError] = useState<string | null>(null);
  const [pickerMode, setPickerMode] = useState<PickerMode>('new');
  const [existingDocs, setExistingDocs] = useState<ExistingDoc[]>([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [selectedExisting, setSelectedExisting] = useState<ExistingDoc | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Candidates for "pick from already uploaded" — only ones not already linked to some other
  // transaction (confirmed default: picking an already-linked document would otherwise silently
  // steal it away with no warning). Fetched once per docType choice, not on every render.
  useEffect(() => {
    if (!docType) { setExistingDocs([]); return; }
    let cancelled = false;
    setLoadingExisting(true);
    fetch(`${config.apiUrl}/api/invoices`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => {
        if (cancelled) return;
        const wantType = docType === 'receipt' ? 'Receipt' : 'Invoice';
        const filtered = (data.invoices || []).filter((inv: any) =>
          inv.document_type === wantType && !inv.bank_transaction_id && !inv.bank_statement_transaction_id
        );
        setExistingDocs(filtered);
      })
      .catch(() => { if (!cancelled) setExistingDocs([]); })
      .finally(() => { if (!cancelled) setLoadingExisting(false); });
    return () => { cancelled = true; };
  }, [docType, token]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles[0]) setFile(acceptedFiles[0]);
  }, []);

  // Same accept sets as ReceiptUpload.tsx/InvoiceUpload.tsx, just capped to a single file —
  // proof always targets exactly one transaction.
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: docType === 'receipt'
      ? { 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'], 'image/webp': ['.webp'], 'image/heic': ['.heic'], 'image/heif': ['.heif'] }
      : { 'application/pdf': ['.pdf'] },
    multiple: false,
  });

  const handleCameraCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setFile(e.target.files[0]);
    e.target.value = '';
  };

  const reset = () => {
    setDocType(null);
    setFile(null);
    setError(null);
    setDuplicateInvoice(null);
    setManualEntryFileInfo(null);
    setManualEntryError(null);
    setPickerMode('new');
    setSelectedExisting(null);
  };

  const handleClose = () => {
    if (uploading) return;
    reset();
    onClose();
  };

  const handleUpload = async () => {
    if (!docType || !file) return;
    setUploading(true);
    setError(null);
    setDuplicateInvoice(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (linkTarget.bank_transaction_id) formData.append('bank_transaction_id', linkTarget.bank_transaction_id);
      if (linkTarget.bank_statement_transaction_id) formData.append('bank_statement_transaction_id', linkTarget.bank_statement_transaction_id);

      const endpoint = docType === 'receipt' ? '/api/receipts/upload' : '/api/invoices/upload';
      const res = await fetch(`${config.apiUrl}${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();

      if (res.ok) {
        onSuccess();
        handleClose();
        return;
      }

      if (res.status === 409 && data.invoice) {
        // This exact file was already uploaded before this feature existed (or twice by
        // accident) — content_hash dedup caught it. Offer to link the existing record instead
        // of leaving the user stuck on a duplicate error with no way to finish.
        setDuplicateInvoice(data.invoice);
        return;
      }

      if (data.manual_entry_available && data.file_info) {
        // Extraction failed but the photo was already saved — carry the link ids through so
        // the manual-entry fallback still attaches it to this transaction. data.error is the
        // extractor's actual reason (e.g. "couldn't read this clearly enough to be confident"),
        // not a generic placeholder — worth showing verbatim rather than a canned string.
        setManualEntryError(data.error || null);
        setManualEntryFileInfo({
          ...data.file_info,
          bank_transaction_id: linkTarget.bank_transaction_id,
          bank_statement_transaction_id: linkTarget.bank_statement_transaction_id,
        });
        return;
      }

      setError(data.error || 'Upload failed');
    } catch {
      setError('Network error — please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Shared by both the duplicate-detection recovery flow (an upload collided with a document
  // that already exists) and the deliberate "pick from already uploaded" picker below — same
  // underlying action either way, just triggered from two different places in the UI.
  const linkExistingDocument = async (invoiceId: string) => {
    setUploading(true);
    setError(null);
    try {
      const res = await fetch(`${config.apiUrl}/api/invoices/${invoiceId}/link-proof`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(linkTarget),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to link existing document');
      }
      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to link existing document');
    } finally {
      setUploading(false);
    }
  };

  const handleLinkExisting = () => { if (duplicateInvoice) linkExistingDocument(duplicateInvoice.id); };
  const handleUseSelectedExisting = () => { if (selectedExisting) linkExistingDocument(selectedExisting.id); };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Upload proof</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: palette.tokens.mutedSoft, mb: 2 }}>
            Attach a receipt or invoice as evidence for this transaction — it'll appear here and in the Invoices/Receipts section below.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {duplicateInvoice ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                This file's already been uploaded as <strong>{duplicateInvoice.contractor_name || 'an existing document'}</strong>
                {duplicateInvoice.invoice_date ? ` (${duplicateInvoice.invoice_date})` : ''}. Use it as proof instead?
              </Typography>
              <Button size="small" variant="contained" onClick={handleLinkExisting} disabled={uploading} sx={{ textTransform: 'none' }}>
                {uploading ? <CircularProgress size={18} /> : 'Use this document'}
              </Button>
            </Alert>
          ) : !docType ? (
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                fullWidth variant="outlined" startIcon={<ReceiptIcon />}
                onClick={() => setDocType('receipt')}
                sx={{ flexDirection: 'column', py: 2.5, borderRadius: 2, textTransform: 'none', borderColor: palette.tokens.hairline, color: palette.tokens.muted, gap: 0.5 }}
              >
                Receipt<br /><Typography variant="caption" sx={{ color: palette.tokens.mutedSoft }}>photo</Typography>
              </Button>
              <Button
                fullWidth variant="outlined" startIcon={<InvoiceIcon />}
                onClick={() => setDocType('invoice')}
                sx={{ flexDirection: 'column', py: 2.5, borderRadius: 2, textTransform: 'none', borderColor: palette.tokens.hairline, color: palette.tokens.muted, gap: 0.5 }}
              >
                Invoice<br /><Typography variant="caption" sx={{ color: palette.tokens.mutedSoft }}>PDF</Typography>
              </Button>
            </Box>
          ) : (
            <Box>
              <Button
                size="small"
                onClick={() => { setDocType(null); setFile(null); setPickerMode('new'); setSelectedExisting(null); }}
                disabled={uploading}
                sx={{ textTransform: 'none', mb: 1.5, color: palette.tokens.muted }}
              >
                ← Change type
              </Button>

              {pickerMode === 'existing' ? (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Pick from your uploaded {docType === 'receipt' ? 'receipts' : 'invoices'}
                  </Typography>
                  {loadingExisting ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={22} />
                    </Box>
                  ) : existingDocs.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 1 }}>
                      No unlinked {docType === 'receipt' ? 'receipts' : 'invoices'} to pick from yet — upload a new one instead.
                    </Alert>
                  ) : (
                    <Autocomplete
                      options={existingDocs}
                      value={selectedExisting}
                      onChange={(_, val) => setSelectedExisting(val)}
                      getOptionLabel={(opt) => opt.contractor_name || 'Untitled'}
                      isOptionEqualToValue={(opt, val) => opt.id === val.id}
                      renderOption={(props, option) => {
                        // MUI v7 embeds `key` inside `props` itself — destructure it out rather
                        // than spreading it alongside an explicit `key`, which triggers a React
                        // console warning ("a props object containing a key... spread into JSX").
                        const { key, ...optionProps } = props;
                        const total = parseFloat(option.total || '0');
                        return (
                          <Box component="li" key={key} {...optionProps} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <ExistingDocThumb id={option.id} docType={docType} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                                {option.contractor_name || 'Untitled'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {Number.isFinite(total) ? `£${total.toFixed(2)}` : option.total}
                                {option.invoice_date ? ` · ${option.invoice_date}` : ''}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField {...params} size="small" placeholder="Search by name…" autoFocus />
                      )}
                    />
                  )}
                  <Button
                    size="small"
                    onClick={() => { setPickerMode('new'); setSelectedExisting(null); }}
                    disabled={uploading}
                    sx={{ mt: 1.5, textTransform: 'none', color: palette.tokens.muted }}
                  >
                    ← Upload a new one instead
                  </Button>
                </Box>
              ) : (
                <>
                  <UploadCaptureControls
                    docType={docType}
                    file={file}
                    isDragActive={isDragActive}
                    getRootProps={getRootProps}
                    getInputProps={getInputProps}
                    cameraInputRef={cameraInputRef}
                    onCameraCapture={handleCameraCapture}
                  />

                  <Divider sx={{ my: 1.5 }}>
                    <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft }}>or</Typography>
                  </Divider>
                  <Button
                    fullWidth
                    size="small"
                    onClick={() => setPickerMode('existing')}
                    disabled={uploading}
                    sx={{ textTransform: 'none', color: palette.tokens.muted }}
                  >
                    Pick one you've already uploaded{existingDocs.length ? ` (${existingDocs.length})` : ''}
                  </Button>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={uploading} sx={{ textTransform: 'none' }}>Cancel</Button>
          {docType && !duplicateInvoice && (
            pickerMode === 'existing' ? (
              <Button variant="contained" onClick={handleUseSelectedExisting} disabled={!selectedExisting || uploading} sx={{ textTransform: 'none', fontWeight: 600 }}>
                {uploading ? <CircularProgress size={20} /> : 'Use this document'}
              </Button>
            ) : (
              <Button variant="contained" onClick={handleUpload} disabled={!file || uploading} sx={{ textTransform: 'none', fontWeight: 600 }}>
                {uploading ? <CircularProgress size={20} /> : 'Upload'}
              </Button>
            )
          )}
        </DialogActions>
      </Dialog>

      {manualEntryFileInfo && (
        <ManualReceiptEntryDialog
          open={!!manualEntryFileInfo}
          onClose={() => { setManualEntryFileInfo(null); setManualEntryError(null); handleClose(); }}
          onSuccess={() => { setManualEntryFileInfo(null); setManualEntryError(null); onSuccess(); handleClose(); }}
          extractionError={manualEntryError || 'Automatic extraction failed'}
          fileInfo={manualEntryFileInfo}
        />
      )}
    </>
  );
};

export default UploadProofDialog;
